import { useWindowSize } from '@website/hooks/useWindowsSize';
import { Media } from '@website/types/graphqlOperations';
import { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FaExpandAlt } from 'react-icons/fa';
import { MdFileDownload } from 'react-icons/md';
import Lightbox from 'react-image-lightbox';
import { composeImageURL, getImageUrl, Image } from '../utils/imgix';

import 'react-image-lightbox/style.css';
import GuestMediaDownloadAll from './GuestMediaDownloadAll';

type Props = {
  images: Pick<Media, 'file' | 'id'>[];
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  colClasses?: string;
  rowClasses?: string;
  showDownload?: boolean;
};

export default function MediaGallery({
  images,
  xs,
  sm,
  lg,
  md,
  xl,
  colClasses,
  rowClasses,
  showDownload = true,
}: Props) {
  const [lightBoxOpen, setLightBoxOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  // we need to stop loading full size images on all devices, here we use the
  // device width as the image size to load
  const { width } = useWindowSize();
  const imgixParams: Record<string, string | number | boolean> = {
    // limit the size to 90% of the device width
    w: width * 0.9,
    fit: 'min',
  };

  const onClick = (imageID: string) => {
    // find the index of the image in the images array
    const imageIndex = images.findIndex((image) => image.id === imageID);
    setActiveImage(imageIndex);
    setLightBoxOpen(!lightBoxOpen);
  };

  const customStyles = {
    overlay: {
      zIndex: '1040',
    },
    bodyOpen: {
      position: 'fixed',
    },
  };

  return (
    <>
      <Row className={rowClasses}>
        {images.map((image) => {
          const url = `${getImageUrl(
            image?.file?.key,
          )}compress,enhance,redeye&crop=faces&w=1920&fit=crop&fm=jpg&q=90&dl`;

          return (
            <Col
              key={image.id}
              xs={xs}
              sm={sm}
              md={md}
              lg={lg}
              xl={xl}
              className={colClasses}
              style={{
                aspectRatio: '3/2',
              }}
            >
              <div
                style={{
                  position: 'relative',
                }}
              >
                <div
                  role="button"
                  onClick={() => onClick(image.id)}
                  onKeyPress={() => onClick(image.id)}
                >
                  <Image
                    key={image.id}
                    imgKey={image.file.key}
                    alt={image.id}
                    className="w-100 img-fluid img-gallery"
                    imgixParams={{
                      ar: '3:2',
                    }}
                    sizes="(min-width: 1400px) 427px, (min-width: 1200px) 367px, (min-width: 992px) 307px, (min-width: 768px) 344px, (min-width: 576px) 254px, 50vw"
                  />
                </div>
                {/* Expand icons */}
                <div
                  className="p-2 m-2 "
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '5px',
                    color: 'rgba(0, 0, 0, 0.5)',
                    pointerEvents: 'none',
                  }}
                >
                  {showDownload && (
                    <Button
                      as="a"
                      href={url}
                      variant="light"
                      aria-label="Download"
                      download
                      className="me-3"
                      style={{
                        background: 'rgba(248, 249, 250,0.6)',
                        border: 'rgba(248, 249, 250, 1)',
                        pointerEvents: 'auto',
                        zIndex: '99999999',
                      }}
                    >
                      <MdFileDownload />
                    </Button>
                  )}
                  <span
                    role="button"
                    onClick={() => onClick(image.id)}
                    onKeyPress={() => onClick(image.id)}
                  >
                    <FaExpandAlt size={20} />
                  </span>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
      <Col className="mt-3 p-0">
        <GuestMediaDownloadAll photos={images} />
      </Col>

      {lightBoxOpen && (
        <Lightbox
          mainSrc={composeImageURL(images[activeImage].file.key, imgixParams)}
          nextSrc={composeImageURL(
            images[(activeImage + 1) % images.length].file.key,
            imgixParams,
          )}
          prevSrc={composeImageURL(
            images[(activeImage + images.length - 1) % images.length].file.key,
            imgixParams,
          )}
          onCloseRequest={() => setLightBoxOpen(false)}
          // imageCaption={data[activeImage].title}
          onMovePrevRequest={() =>
            setActiveImage((activeImage + images.length - 1) % images.length)
          }
          onMoveNextRequest={() =>
            setActiveImage((activeImage + 1) % images.length)
          }
          enableZoom={false}
          reactModalStyle={customStyles}
        />
      )}
    </>
  );
}
